import React, { useState, useEffect, useRef, useMemo, useLayoutEffect } from 'react';
import { Card, GameState, PlayerSeat, TrickPlay } from '../../core/types';
import { PlayingCard } from '../Card/PlayingCard';
import { CardBack } from '../Card/CardBack';
import { describeCombo } from '../../core/combos';
import { choosePlan } from '../../core/optimizer';
import { Sound } from '../../core/audio';
import { Voice, POPULAR_EMOJIS, POPULAR_PHRASES, VoicePhrase } from '../../core/voice';
import { sortHand } from '../../core/cards';
import { CardBackCustomizerModal } from '../Card/CardBackCustomizerModal';
import { useI18n } from '../../core/i18n';
import {
  Crown,
  Sparkles,
  Volume2,
  VolumeX,
  X,
  Eye,
  EyeOff,
  Smile,
  MessageCircle,
  Zap,
  Layers,
  ArrowDownWideNarrow,
  Plus,
  RotateCcw,
  Maximize2,
  Minimize2,
  Palette,
} from 'lucide-react';

export type GroupingMode = 'natural' | 'coach' | 'manual';

interface PokerTableProps {
  gameState: GameState;
  selectedIds: Set<string>;
  onToggleCard: (cardId: string) => void;
  onClearSelection: () => void;
  onPlay: () => void;
  onPass: () => void;
  onAutoHint: () => void;
  onToggleGodMode?: () => void;
  onSendEmoji?: (seat: PlayerSeat, emoji: string, text?: string) => void;
}

export const PokerTable: React.FC<PokerTableProps> = ({
  gameState,
  selectedIds,
  onToggleCard,
  onClearSelection,
  onPlay,
  onPass,
  onAutoHint,
  onToggleGodMode,
  onSendEmoji,
}) => {
  const { t } = useI18n();
  const [groupingMode, setGroupingMode] = useState<GroupingMode>(() => {
    const saved = localStorage.getItem('guandan_grouping_mode');
    if (saved === 'natural' || saved === 'coach' || saved === 'manual') return saved;
    return 'coach';
  });
  const [isLandscapeMode, setIsLandscapeMode] = useState<boolean>(false);
  const [customGroups, setCustomGroups] = useState<Card[][]>([]);
  const [isMuted, setIsMuted] = useState<boolean>(() => Sound.getIsMuted());
  const [showEmojiPicker, setShowEmojiPicker] = useState<boolean>(false);
  const [showVoicePicker, setShowVoicePicker] = useState<boolean>(false);
  const [showCardBackModal, setShowCardBackModal] = useState<boolean>(false);
  const [activeEmojis, setActiveEmojis] = useState<Record<number, { emoji: string; text?: string } | null>>({});
  const [countdown, setCountdown] = useState<number>(20);
  const [isDealing, setIsDealing] = useState<boolean>(true);
  const [dealProgress, setDealProgress] = useState<number>(0);

  const {
    hands,
    currentTurn,
    currentCombo,
    trickPlays,
    levelRank,
    finishedOrder,
    isGodMode,
    mode = '4p',
  } = gameState;

  const is6p = mode === '6p';
  const userHand = hands[0] || [];
  const isMyTurn = currentTurn === 0;

  // Trigger distribution / dealing animation on fresh hand start or mode switch
  useEffect(() => {
    if (gameState.history.length === 0 && userHand.length > 0) {
      setIsDealing(true);
      setDealProgress(1);
      let count = 1;
      const totalCards = userHand.length;

      const dealTimer = setInterval(() => {
        count += 1;
        if (count >= totalCards) {
          count = totalCards;
          setDealProgress(totalCards);
          setIsDealing(false);
          clearInterval(dealTimer);
          Sound.playCardPlay();
        } else {
          setDealProgress(count);
          if (count % 2 === 0) {
            Sound.playCardDeal();
          }
        }
      }, 78);

      return () => clearInterval(dealTimer);
    } else {
      setIsDealing(false);
      setDealProgress(userHand.length);
    }
  }, [gameState.initialHands, mode, levelRank]);

  const handleSkipDealing = () => {
    setIsDealing(false);
    setDealProgress(userHand.length);
    Sound.playCardPlay();
  };

  const handleSelectGroupingMode = (mode: GroupingMode) => {
    setGroupingMode(mode);
    localStorage.setItem('guandan_grouping_mode', mode);
  };

  const handleToggleLandscape = () => {
    setIsLandscapeMode((prev) => !prev);
    Sound.playCardClick();
  };

  const tableRef = useRef<HTMLDivElement>(null);
  const [tableDimensions, setTableDimensions] = useState({ width: 1000, height: 650 });

  useLayoutEffect(() => {
    if (!tableRef.current) return;
    const updateDims = () => {
      if (tableRef.current) {
        const { clientWidth, clientHeight } = tableRef.current;
        if (clientWidth > 0 && clientHeight > 0) {
          setTableDimensions({ width: clientWidth, height: clientHeight });
        }
      }
    };
    updateDims();
    const observer = new ResizeObserver(updateDims);
    observer.observe(tableRef.current);
    window.addEventListener('resize', updateDims);
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', updateDims);
    };
  }, []);

  // Pure parametric geometric calculation engine for responsive table scaling
  const tableLayout = useMemo(() => {
    const { width, height } = tableDimensions;
    const aspect = width / Math.max(height, 1);

    // Dynamic radii rx, ry based on real measured aspect ratio
    let rx = 0.38;
    let ry = 0.36;
    let scale = 1.0;

    if (aspect <= 0.75) {
      // 9:16 or 9:19.5 Mobile Portrait
      rx = 0.40;
      ry = 0.30;
      scale = Math.max(0.72, Math.min(1.05, width / 390));
    } else if (aspect >= 1.7) {
      // 19.5:9 or ultra-wide landscape
      rx = 0.36;
      ry = 0.33;
      scale = Math.max(0.70, Math.min(1.10, height / 420));
    } else {
      // Standard Desktop / Tablet
      rx = 0.38;
      ry = 0.35;
      scale = Math.max(0.80, Math.min(1.30, Math.min(width / 1100, height / 700)));
    }

    const isCompactHeight = height < 450;
    const isMobilePortrait = aspect <= 0.75;

    // 16:9 Widescreen Stadium Hexagon Vertices
    let v1 = { x: 30, y: 13 };
    let v2 = { x: 70, y: 13 };
    let v3 = { x: 92, y: 50 };
    let v4 = { x: 70, y: 87 };
    let v5 = { x: 30, y: 87 };
    let v6 = { x: 8, y: 50 };

    if (isMobilePortrait) {
      // 9:16 Portrait Phone
      v1 = { x: 22, y: 15 };
      v2 = { x: 78, y: 15 };
      v3 = { x: 94, y: 50 };
      v4 = { x: 78, y: 85 };
      v5 = { x: 22, y: 85 };
      v6 = { x: 6, y: 50 };
    } else if (isCompactHeight) {
      // Mobile Landscape (e.g. iPhone horizontal)
      v1 = { x: 26, y: 11 };
      v2 = { x: 74, y: 11 };
      v3 = { x: 95, y: 50 };
      v4 = { x: 74, y: 89 };
      v5 = { x: 26, y: 89 };
      v6 = { x: 5, y: 50 };
    }

    const hexPolygonPoints = `${v1.x},${v1.y} ${v2.x},${v2.y} ${v3.x},${v3.y} ${v4.x},${v4.y} ${v5.x},${v5.y} ${v6.x},${v6.y}`;

    // Seats are mathematically anchored at the EXACT midpoints of the 6 edges
    const seats6p = {
      3: { x: (v1.x + v2.x) / 2, y: (v1.y + v2.y) / 2 }, // North
      2: { x: (v2.x + v3.x) / 2, y: (v2.y + v3.y) / 2 }, // Northeast
      1: { x: (v3.x + v4.x) / 2, y: (v3.y + v4.y) / 2 }, // Southeast
      0: { x: (v4.x + v5.x) / 2, y: (v4.y + v5.y) / 2 }, // South
      5: { x: (v5.x + v6.x) / 2, y: (v5.y + v6.y) / 2 }, // Southwest
      4: { x: (v6.x + v1.x) / 2, y: (v6.y + v1.y) / 2 }, // Northwest
    };

    const avatarSize = isCompactHeight ? 22 : Math.round(36 * scale);
    const fontSize = isCompactHeight ? 7.5 : Math.round(10.5 * scale);
    const badgeSize = isCompactHeight ? 6.5 : Math.round(8.5 * scale);

    return {
      aspect,
      scale,
      isCompactHeight,
      isMobilePortrait,
      avatarSize,
      fontSize,
      badgeSize,
      hexPolygonPoints,
      seats6p,
      width,
      height
    };
  }, [tableDimensions]);

  useEffect(() => {
    setCountdown(20);
    const interval = setInterval(() => {
      setCountdown((prev) => (prev > 0 ? prev - 1 : 20));
    }, 1000);
    return () => clearInterval(interval);
  }, [currentTurn]);

  useEffect(() => {
    const userHandCardIds = new Set(userHand.map((c) => c.id));
    setCustomGroups((prevGroups) =>
      prevGroups
        .map((group) => group.filter((c) => userHandCardIds.has(c.id)))
        .filter((group) => group.length > 0)
    );
  }, [userHand]);

  const visibleHand =
    isDealing && gameState.history.length === 0
      ? userHand.slice(0, Math.max(1, dealProgress))
      : userHand;
  const planResult = choosePlan(visibleHand, levelRank);
  const bestGroups = planResult.best.groups;
  const naturalSortedHand = sortHand([...visibleHand], levelRank, true);

  const activeCustomGroupCardIds = new Set(customGroups.flatMap((g) => g.map((c) => c.id)));
  const ungroupedCards = naturalSortedHand.filter((c) => !activeCustomGroupCardIds.has(c.id));

  const handleCreateCustomGroup = () => {
    if (selectedIds.size === 0) return;
    const cardsToGroup = userHand.filter((c) => selectedIds.has(c.id));
    if (cardsToGroup.length === 0) return;

    const cleanOldGroups = customGroups
      .map((g) => g.filter((c) => !selectedIds.has(c.id)))
      .filter((g) => g.length > 0);

    setCustomGroups([...cleanOldGroups, cardsToGroup]);
    onClearSelection();
    Sound.playCardPlay();
  };

  const handleDissolveCustomGroup = (index: number) => {
    setCustomGroups((prev) => prev.filter((_, idx) => idx !== index));
    Sound.playCardClick();
  };

  const handleResetCustomGroups = () => {
    setCustomGroups([]);
    onClearSelection();
    Sound.playCardClick();
  };

  const seatNames4p: Record<number, { name: string; pos: string; role: string }> = {
    0: { name: '我 (南)', pos: '南', role: '己方主攻' },
    1: { name: '东家 (右家)', pos: '东', role: '对方下家' },
    2: { name: '搭档 (北门)', pos: '北', role: '对门搭档' },
    3: { name: '西家 (左家)', pos: '西', role: '对方上家' },
  };

  const seatNames6p: Record<number, { name: string; pos: string; role: string }> = {
    0: { name: '我 (南)', pos: '南', role: '主队1' },
    1: { name: '东1 (东南)', pos: '东南', role: '客队1' },
    2: { name: '搭档1 (东北)', pos: '东北', role: '主队2' },
    3: { name: '北家 (正北)', pos: '正北', role: '客队2' },
    4: { name: '搭档2 (西北)', pos: '西北', role: '主队3' },
    5: { name: '西1 (西南)', pos: '西南', role: '客队3' },
  };

  const seatNames = is6p ? seatNames6p : seatNames4p;

  const handleToggleSound = () => {
    const next = Sound.toggleMute();
    setIsMuted(next);
  };

  const triggerSeatEmoji = (seat: PlayerSeat, emoji: string, text?: string) => {
    setActiveEmojis((prev) => ({ ...prev, [seat]: { emoji, text } }));
    if (text) {
      Voice.speak(text);
    } else {
      Sound.playCardPlay();
    }
    if (onSendEmoji) onSendEmoji(seat, emoji, text);

    setTimeout(() => {
      setActiveEmojis((prev) => ({ ...prev, [seat]: null }));
    }, 3500);
  };

  const handleSelectEmoji = (item: (typeof POPULAR_EMOJIS)[0]) => {
    triggerSeatEmoji(0 as PlayerSeat, item.emoji, item.label);
    setShowEmojiPicker(false);
  };

  const handleSelectPhrase = (phrase: VoicePhrase) => {
    triggerSeatEmoji(0 as PlayerSeat, '💬', phrase.text);
    Voice.triggerPhrase(phrase);
    setShowVoicePicker(false);
  };

  // Render Per-Seat Dedicated Trick Plays & Persistent Action Status (or Dealing Card Pile during distribution)
  const renderSeatTrickAction = (seatIndex: number, extraClasses = '') => {
    if (isDealing) {
      return (
        <div className={`flex flex-col items-center animate-fade-in pointer-events-none select-none ${extraClasses}`}>
          {/* 3D Stacked Card Pile before each player */}
          <div className="relative flex items-center justify-center">
            <div className="absolute transform translate-x-1 -translate-y-1 rotate-3 opacity-70">
              <CardBack size="sm" />
            </div>
            <div className="relative transform -translate-x-0.5 translate-y-0.5 -rotate-2 shadow-2xl">
              <CardBack size="sm" />
            </div>
          </div>
          <div className="mt-0.5 bg-black/85 text-amber-300 border border-amber-500/40 px-1.5 py-0.2 rounded-full text-[8px] sm:text-[9px] font-mono font-black shadow-md flex items-center gap-0.5">
            <span>🎴</span>
            <span>{dealProgress}/{userHand.length}</span>
          </div>
        </div>
      );
    }

    const trick = trickPlays[seatIndex];
    if (!trick) return null;

    if (trick.action === 'pass') {
      return (
        <div
          className={`animate-fade-in bg-rose-950/90 text-rose-300 border border-rose-500/80 font-black text-[9px] sm:text-xs px-2 py-0.5 rounded-full shadow-lg flex items-center space-x-0.5 backdrop-blur-md ${extraClasses}`}
        >
          <span className="text-rose-400">🛑</span>
          <span>{t.passBtn}</span>
        </div>
      );
    }

    if (trick.action === 'play' && trick.cards && trick.cards.length > 0) {
      return (
        <div className={`flex flex-col items-center animate-fade-in space-y-0.5 ${extraClasses}`}>
          {trick.combo && (
            <div className="text-[8px] sm:text-[9px] font-black px-1.5 py-0.1 rounded-full bg-black/85 text-amber-300 border border-amber-500/40 shadow-sm flex items-center gap-0.5">
              <span>{describeCombo(trick.combo)}</span>
              {trick.combo.isBomb && <span className="text-rose-400 font-black">🔥</span>}
            </div>
          )}
          <div className="flex -space-x-3 sm:-space-x-4 drop-shadow-lg">
            {trick.cards.map((c) => (
              <PlayingCard key={c.id} card={c} levelRank={levelRank} size="sm" disabled />
            ))}
          </div>
        </div>
      );
    }

    return null;
  };

  // Render Seat Avatar & Info HUD with Active Turn Spotlight & Red/Blue Team Colors
  const renderSeatAvatar = (seatIndex: number, extraClasses = '') => {
    const isCurrent = currentTurn === seatIndex;
    const isFinished = finishedOrder.includes(seatIndex as PlayerSeat);
    const finishRank = finishedOrder.indexOf(seatIndex as PlayerSeat) + 1;
    const count = hands[seatIndex]?.length || 0;
    const seatInfo = seatNames[seatIndex] || { name: `Seat ${seatIndex}`, pos: '', role: '' };
    
    // Team 0 (红方): 0, 2, 4 | Team 1 (蓝方): 1, 3, 5
    const isRedTeam = seatIndex % 2 === 0;
    const isPartner = isRedTeam && seatIndex !== 0;
    const activeMsg = activeEmojis[seatIndex];

    return (
      <div className={`relative flex flex-col items-center select-none ${extraClasses}`}>
        {/* Dynamic Speech / Emoji Bubble */}
        {activeMsg && (
          <div className="absolute -top-7 z-40 bg-white text-slate-900 px-1.5 py-0.2 rounded-xl shadow-xl border border-amber-300 font-bold text-[10px] flex items-center space-x-1 animate-bounce whitespace-nowrap">
            <span>{activeMsg.emoji}</span>
            {activeMsg.text && <span>{activeMsg.text}</span>}
          </div>
        )}

        {/* Circular Avatar with Red / Blue Team Glow */}
        <div className="relative flex items-center justify-center">
          {isCurrent && (
            <div className="absolute -inset-1.5 rounded-full bg-amber-400/30 blur-sm animate-pulse pointer-events-none" />
          )}

          <div
            className={`w-7.5 h-7.5 sm:w-10 sm:h-10 rounded-full flex items-center justify-center shadow-lg transition-all duration-300 z-10 ${
              isCurrent
                ? 'ring-2 ring-amber-400 bg-gradient-to-tr from-amber-500 via-amber-300 to-amber-600 scale-105 shadow-[0_0_15px_rgba(245,158,11,0.8)]'
                : isRedTeam
                ? 'ring-1.5 ring-rose-500/80 bg-gradient-to-tr from-rose-900 via-rose-800 to-rose-950 shadow-[0_0_10px_rgba(244,63,94,0.3)]'
                : 'ring-1.5 ring-sky-500/80 bg-gradient-to-tr from-sky-900 via-sky-800 to-sky-950 shadow-[0_0_10px_rgba(14,165,233,0.3)]'
            }`}
          >
            <span className="text-sm sm:text-base filter drop-shadow">
              {seatIndex === 0 ? '😎' : isPartner ? '🤝' : '🤖'}
            </span>

            {isCurrent && (
              <div className="absolute -bottom-1 -right-1 bg-amber-500 text-slate-950 font-black text-[7px] sm:text-[8px] w-3.5 h-3.5 rounded-full flex items-center justify-center border border-amber-200 shadow">
                {countdown}
              </div>
            )}
          </div>

          {isFinished && (
            <div className="absolute -top-1.5 -right-1.5 bg-gradient-to-r from-amber-400 to-amber-600 text-slate-950 font-black text-[8px] px-1 py-0.1 rounded-full shadow border border-amber-200 z-20">
              {finishRank === 1 ? '🥇' : finishRank === 2 ? '🥈' : '🥉'}
            </div>
          )}
        </div>

        {/* Seat Label Pill & Remaining Count with Red / Blue Team Badge */}
        <div className="mt-0.5 flex items-center space-x-0.5 whitespace-nowrap shrink-0">
          <span
            className={`px-1 py-0.2 rounded text-[8px] sm:text-[9.5px] font-black leading-none flex items-center space-x-0.5 whitespace-nowrap shadow-sm ${
              isCurrent
                ? 'bg-amber-500 text-slate-950'
                : isRedTeam
                ? 'bg-rose-950/90 text-rose-200 border border-rose-600/50'
                : 'bg-sky-950/90 text-sky-200 border border-sky-600/50'
            }`}
          >
            <span className="text-[7px]">{isRedTeam ? '🔴' : '🔵'}</span>
            <span className="whitespace-nowrap">{seatInfo.name}</span>
          </span>
          {/* Show Card Count only when <= 7 (4P) or <= 10 (6P), or in God Mode */}
          {!isFinished && seatIndex !== 0 && (isGodMode || count <= (is6p ? 10 : 7)) && (
            <span
              className={`text-[8px] sm:text-[8.5px] font-mono font-black px-1 py-0.1 rounded border whitespace-nowrap shrink-0 flex items-center space-x-0.5 ${
                count <= 2
                  ? 'bg-rose-600 text-white border-rose-300 animate-pulse shadow-[0_0_8px_rgba(225,29,72,0.8)]'
                  : count <= 5
                  ? 'bg-amber-600 text-amber-100 border-amber-300 shadow-sm'
                  : isRedTeam
                  ? 'bg-rose-950/90 text-rose-300 border-rose-500/40'
                  : 'bg-sky-950/90 text-sky-300 border-sky-500/40'
              }`}
            >
              {count <= 2 && <span className="text-[7px]">🚨</span>}
              {count > 2 && count <= 5 && <span className="text-[7px]">⚠️</span>}
              <span>余{count}</span>
            </span>
          )}
        </div>
      </div>
    );
  };

  const renderAdaptiveCards = (cards: Card[]) => {
    const total = cards.length;
    if (total === 0) return null;

    const isCompact = tableLayout.isCompactHeight;
    const cardSize = isCompact ? 'sm' : 'md';
    // Base physical width of one card: 'sm' is 30px, 'md' is 44px (up to 52px on larger desktop)
    const cardWidth = isCompact ? 30 : Math.min(50, Math.max(38, Math.round(44 * tableLayout.scale)));

    // Reserve safe breathing margin between border cards and desktop edge
    const safeMargin = Math.max(16, Math.min(36, Math.round(tableDimensions.width * 0.04)));
    const availableWidth = Math.max(260, tableDimensions.width - 2 * safeMargin);

    // Calculate exact overlap step so (total - 1) * step + cardWidth <= availableWidth
    let step = cardWidth;
    if (total > 1) {
      const maxStep = (availableWidth - cardWidth) / (total - 1);
      step = Math.min(cardWidth, Math.max(18, maxStep));
    }
    const overlapMarginPx = Math.round(step - cardWidth);

    return (
      <div className="flex items-end justify-center w-full px-3 sm:px-6 md:px-8 overflow-visible">
        {cards.map((c, idx) => {
          const isLast = idx === total - 1;
          const overlapStyle: React.CSSProperties = isLast
            ? { zIndex: idx + 1 }
            : {
                marginRight: `${overlapMarginPx}px`,
                zIndex: idx + 1,
              };

          return (
            <div key={c.id} style={overlapStyle} className="transition-all duration-150 shrink-0">
              <PlayingCard
                card={c}
                levelRank={levelRank}
                isSelected={selectedIds.has(c.id)}
                onClick={() => onToggleCard(c.id)}
                size={cardSize}
              />
            </div>
          );
        })}
      </div>
    );
  };

  const renderGroupedCards = (groups: { cards: Card[] }[], isCustom = false) => {
    if (!groups || groups.length === 0) return null;

    const totalCards = groups.reduce((sum, g) => sum + g.cards.length, 0);
    const groupCount = groups.length;
    const isCompact = tableLayout.isCompactHeight;

    // Measured usable width reserved with guaranteed breathing borders
    const safeMargin = Math.max(16, Math.min(40, Math.round(tableDimensions.width * 0.04)));
    const usableWidth = Math.max(260, tableDimensions.width - 2 * safeMargin);

    // If usableWidth is tight for many groups, dynamically select 'sm' card size
    const useSmallCard = isCompact || (groupCount >= 7 && usableWidth < 880) || (usableWidth < 700);
    const cardSize = useSmallCard ? 'sm' : 'md';
    const cardBaseWidth = useSmallCard ? 30 : 44;

    // Dynamic negative step inside each group so cards cascade gracefully with full corner pips
    const intraStep = useSmallCard
      ? Math.max(14, Math.min(22, Math.round((usableWidth - groupCount * 10) / Math.max(totalCards, 1))))
      : Math.max(18, Math.min(28, Math.round((usableWidth - groupCount * 12) / Math.max(totalCards, 1))));
    const intraMarginPx = Math.round(intraStep - cardBaseWidth);

    return (
      <div className="flex flex-nowrap items-end justify-center gap-0.5 sm:gap-1 max-w-full px-4 sm:px-8 overflow-visible">
        {groups.map((group, gIdx) => (
          <div
            key={gIdx}
            className={`relative flex items-end p-0.5 ${
              isCustom ? 'bg-sky-950/40 border border-sky-500/40' : 'bg-black/40 border border-amber-500/30'
            } rounded-xl shrink-0 shadow-md`}
          >
            {isCustom && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  handleDissolveCustomGroup(gIdx);
                }}
                className="absolute -top-2 left-1/2 -translate-x-1/2 bg-sky-900 text-white text-[8px] px-1 rounded-full border border-sky-400 z-30"
              >
                ✕
              </button>
            )}
            {group.cards.map((c, cIdx) => {
              const isLastInGroup = cIdx === group.cards.length - 1;
              return (
                <div
                  key={c.id}
                  style={isLastInGroup ? { zIndex: cIdx + 1 } : { marginRight: `${intraMarginPx}px`, zIndex: cIdx + 1 }}
                  className="transition-all duration-150 shrink-0"
                >
                  <PlayingCard
                    card={c}
                    levelRank={levelRank}
                    isSelected={selectedIds.has(c.id)}
                    onClick={() => onToggleCard(c.id)}
                    size={cardSize}
                  />
                </div>
              );
            })}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div
      ref={tableRef}
      className={`relative select-none overflow-hidden font-sans transition-all duration-300 w-full h-full flex flex-col justify-between ${
        isLandscapeMode
          ? 'fixed inset-0 z-50 rounded-none border-none shadow-none p-1'
          : 'rounded-2xl sm:rounded-3xl border-2 sm:border-4 border-[#2b1f14] shadow-[0_20px_50px_rgba(0,0,0,0.85)] p-1'
      }`}
    >
      {/* Table Background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(ellipse at 50% 40%, #1c664b 0%, #0f4531 45%, #082d20 80%, #041811 100%)',
          boxShadow: 'inset 0 0 100px rgba(0, 0, 0, 0.75)',
        }}
      >
        <div
          className="absolute inset-0 opacity-8 pointer-events-none"
          style={{
            backgroundImage: `radial-gradient(#ffffff 1px, transparent 1px)`,
            backgroundSize: '24px 24px',
          }}
        />
      </div>

      {/* 6-Player Parametric Hexagonal Table Background Overlay */}
      {is6p && (
        <div className="absolute inset-0 pointer-events-none z-0 flex items-center justify-center">
          <svg
            viewBox="0 0 100 100"
            className="w-full h-full opacity-45 drop-shadow-[0_0_24px_rgba(245,158,11,0.4)]"
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="hexGoldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#f59e0b" />
                <stop offset="50%" stopColor="#fde68a" />
                <stop offset="100%" stopColor="#d97706" />
              </linearGradient>
            </defs>
            {/* Hexagon Outer Trim (Parametrically calculated to screen aspect ratio) */}
            <polygon
              points={tableLayout.hexPolygonPoints}
              fill="rgba(4, 35, 23, 0.6)"
              stroke="url(#hexGoldGrad)"
              strokeWidth="1.4"
            />
          </svg>
        </div>
      )}

      {/* Top Bar: North Seats & Tools */}
      <div className="relative z-20 px-1 pt-0.5 flex items-center justify-between shrink-0 h-11 sm:h-13">
        {/* Quick Tools */}
        <div className="flex items-center space-x-1 bg-slate-950/80 backdrop-blur-md p-0.5 rounded-xl border border-emerald-500/30 shadow">
          <button
            onClick={() => setShowEmojiPicker(!showEmojiPicker)}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-amber-400 rounded-lg shadow"
            title="快捷表情"
          >
            <Smile className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={() => setShowVoicePicker(!showVoicePicker)}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-sky-400 rounded-lg shadow"
            title="实战语音交流"
          >
            <MessageCircle className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleToggleSound}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-slate-300 rounded-lg shadow"
            title={isMuted ? '开启音效' : '静音'}
          >
            {isMuted ? <VolumeX className="w-3.5 h-3.5 text-rose-400" /> : <Volume2 className="w-3.5 h-3.5" />}
          </button>
          <button
            onClick={() => setShowCardBackModal(true)}
            className="p-1 bg-slate-900 hover:bg-slate-800 text-amber-300 rounded-lg shadow border border-amber-500/20"
            title="定制专属牌背与Logo"
          >
            <Palette className="w-3.5 h-3.5" />
          </button>
          <button
            onClick={handleToggleLandscape}
            className={`px-1.5 py-0.5 rounded-lg text-[9px] sm:text-[10px] font-black flex items-center gap-0.5 shadow ${
              isLandscapeMode
                ? 'bg-amber-500 text-slate-950'
                : 'bg-slate-900 text-amber-400 border border-amber-500/30'
            }`}
            title={isLandscapeMode ? t.exitLandscape : t.modeLandscape}
          >
            {isLandscapeMode ? <Minimize2 className="w-3 h-3" /> : <Maximize2 className="w-3 h-3" />}
            <span className="hidden sm:inline">{isLandscapeMode ? t.exitLandscape : t.modeLandscape}</span>
          </button>
          {onToggleGodMode && (
            <button
              onClick={onToggleGodMode}
              className={`p-1 rounded-lg shadow ${
                isGodMode ? 'bg-amber-500 text-slate-950 font-black' : 'bg-slate-900 text-slate-400'
              }`}
              title="上帝视角"
            >
              {isGodMode ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
            </button>
          )}
        </div>

        {/* Top Center: 4P North Seat vs 6P Arena Mode Title */}
        {!is6p ? (
          <div className="flex flex-col items-center">
            {renderSeatAvatar(2)}
            <div className="h-5 flex items-center justify-center">
              {renderSeatTrickAction(2)}
            </div>
          </div>
        ) : (
          <div className="hidden lg:flex items-center space-x-1 bg-slate-950/80 backdrop-blur-md px-2.5 py-0.5 rounded-xl border border-amber-500/40 shadow">
            <span className="text-amber-400 font-black text-[10px]">⚔️ 6人团战竞技台</span>
          </div>
        )}

        {/* Grade Indicator */}
        <div className="bg-slate-950/80 backdrop-blur-md px-2 py-0.5 rounded-xl border border-amber-500/40 shadow flex items-center space-x-1 text-[9px] sm:text-[10px]">
          <Crown className="w-3 h-3 text-amber-400" />
          <span className="text-white font-black">打【{levelRank}】</span>
          <span className="text-rose-400 font-bold">♥{levelRank}配</span>
        </div>
      </div>

      {/* Central Felt Playing Arena (Unified Coordinate Canvas) */}
      <div className="relative z-10 flex-1 w-full min-h-0 flex flex-col items-center justify-center">
        {/* 6-Player Absolute Seating on Mathematical Hexagon Edges */}
        {is6p && (
          <div className="absolute inset-0 pointer-events-none z-15">
            {/* North (Seat 3 正北·客队2) */}
            <div
              className="absolute pointer-events-auto flex flex-col items-center -translate-x-1/2 -translate-y-1/2 z-25"
              style={{ left: `${tableLayout.seats6p[3].x}%`, top: `${tableLayout.seats6p[3].y}%` }}
            >
              {renderSeatAvatar(3)}
              <div className="min-w-[42px] flex items-center justify-center mt-1 z-30 drop-shadow-xl">
                {renderSeatTrickAction(3)}
              </div>
            </div>

            {/* NW Flank (Seat 4 西北·搭档2): Played cards a bit top-right */}
            <div
              className="absolute pointer-events-auto flex items-center space-x-1 -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ left: `${tableLayout.seats6p[4].x}%`, top: `${tableLayout.seats6p[4].y}%` }}
            >
              {renderSeatAvatar(4)}
              <div className="min-w-[42px] flex items-center justify-center -translate-y-2.5 translate-x-1 sm:-translate-y-3 sm:translate-x-2 drop-shadow-xl">
                {renderSeatTrickAction(4)}
              </div>
            </div>

            {/* NE Flank (Seat 2 东北·搭档1): Played cards top-left */}
            <div
              className="absolute pointer-events-auto flex items-center space-x-1 -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ left: `${tableLayout.seats6p[2].x}%`, top: `${tableLayout.seats6p[2].y}%` }}
            >
              <div className="min-w-[42px] flex items-center justify-center -translate-y-2.5 -translate-x-1 sm:-translate-y-3 sm:-translate-x-2 drop-shadow-xl">
                {renderSeatTrickAction(2)}
              </div>
              {renderSeatAvatar(2)}
            </div>

            {/* SW Flank (Seat 5 西南·客队3): Played cards a bit bottom-left */}
            <div
              className="absolute pointer-events-auto flex items-center space-x-1 -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ left: `${tableLayout.seats6p[5].x}%`, top: `${tableLayout.seats6p[5].y}%` }}
            >
              <div className="min-w-[42px] flex items-center justify-center translate-y-2.5 -translate-x-1 sm:translate-y-3 sm:-translate-x-2 drop-shadow-xl">
                {renderSeatTrickAction(5)}
              </div>
              {renderSeatAvatar(5)}
            </div>

            {/* SE Flank (Seat 1 东南·客队1): Played cards bottom-right */}
            <div
              className="absolute pointer-events-auto flex items-center space-x-1 -translate-x-1/2 -translate-y-1/2 z-20"
              style={{ left: `${tableLayout.seats6p[1].x}%`, top: `${tableLayout.seats6p[1].y}%` }}
            >
              {renderSeatAvatar(1)}
              <div className="min-w-[42px] flex items-center justify-center translate-y-2.5 translate-x-1 sm:translate-y-3 sm:translate-x-2 drop-shadow-xl">
                {renderSeatTrickAction(1)}
              </div>
            </div>
          </div>
        )}

        {/* Center Area: Status Podium & 4P Flanks */}
        <div className="relative z-10 w-full h-full flex items-center justify-between px-1 sm:px-4 min-h-0">
          {/* 4-Player West Seat + Drop Zone */}
          {!is6p ? (
            <div className="flex items-center space-x-1">
              {renderSeatAvatar(3)}
              <div className="min-w-[45px] sm:min-w-[55px] flex items-center justify-center">
                {renderSeatTrickAction(3)}
              </div>
            </div>
          ) : (
            <div className="w-1" />
          )}

        {/* Center Podium: Dealing Animation or Match Status & South's Last Played Cards */}
        <div className="self-center flex flex-col items-center justify-center">
          {isDealing ? (
            <div
              onClick={handleSkipDealing}
              className="flex flex-col items-center justify-center p-2 rounded-2xl bg-black/70 border border-amber-500/50 backdrop-blur-md shadow-2xl mx-auto cursor-pointer animate-fade-in z-30 select-none group transition-transform hover:scale-105"
              title="点击跳过发牌动画"
            >
              {/* Animated 3D Dealing Deck with larger authentic CardBacks & flying cards */}
              <div className="relative flex items-center justify-center my-1">
                <div className="absolute w-20 h-20 rounded-full bg-amber-400/25 blur-xl animate-pulse" />

                {/* Center Deck 3D Stack */}
                <div className="relative select-none transform transition-transform group-hover:scale-105">
                  <div className="absolute transform translate-x-1.5 -translate-y-1.5 rotate-6 opacity-60">
                    <CardBack size={tableLayout.isCompactHeight ? 'sm' : 'md'} />
                  </div>
                  <div className="absolute transform -translate-x-1 translate-y-1 -rotate-3 opacity-80">
                    <CardBack size={tableLayout.isCompactHeight ? 'sm' : 'md'} />
                  </div>
                  <div className="relative shadow-2xl">
                    <CardBack size={tableLayout.isCompactHeight ? 'sm' : 'md'} />
                  </div>
                </div>

                {/* Flying CardBack Cards with authentic Logo to all players */}
                {is6p ? (
                  <>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlyNorth 0.7s ease-out infinite' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlyNE 0.7s ease-out infinite 0.12s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlySE 0.7s ease-out infinite 0.24s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlySouth 0.7s ease-out infinite 0.36s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlySW 0.7s ease-out infinite 0.48s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlyNW 0.7s ease-out infinite 0.60s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlyNorth 0.7s ease-out infinite' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlySouth 0.7s ease-out infinite 0.18s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlyWest 0.7s ease-out infinite 0.36s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                    <div
                      className="absolute pointer-events-none drop-shadow-2xl"
                      style={{ animation: 'dealFlyEast 0.7s ease-out infinite 0.54s' }}
                    >
                      <CardBack size="sm" />
                    </div>
                  </>
                )}
              </div>

              {/* Dealing Status Pill with Full Count */}
              <div className="mt-1.5 bg-amber-500/25 text-amber-300 border border-amber-500/50 px-2.5 py-0.5 rounded-full text-[9px] sm:text-[10.5px] font-black flex items-center space-x-1 shadow-lg">
                <Sparkles className="w-3 h-3 text-amber-400 animate-spin" />
                <span>
                  {is6p
                    ? `6人发牌 (${dealProgress}/27 · 共162张)`
                    : `4人发牌 (${dealProgress}/27 · 共108张)`}
                </span>
                <span className="text-[8.5px] text-slate-300 group-hover:text-white underline ml-1 font-bold">跳过</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-0.5 px-2 rounded-xl bg-black/45 border border-amber-500/30 backdrop-blur-md shadow-xl mx-auto max-w-xs text-center">
              <div className="flex items-center space-x-1 text-[9px] sm:text-[10px] font-black">
                <span className="text-amber-400">⚡ 轮到：</span>
                <span
                  className={`px-1 py-0.1 rounded ${
                    isMyTurn
                      ? 'bg-amber-500 text-slate-950 font-black'
                      : currentTurn % 2 === 0
                      ? 'text-rose-300 font-extrabold'
                      : 'text-sky-300 font-extrabold'
                  }`}
                >
                  {seatNames[currentTurn]?.name || `Seat ${currentTurn}`}
                </span>
              </div>

              {currentCombo ? (
                <div className="mt-0.5 flex items-center space-x-1 text-[8px] sm:text-[9px] bg-amber-500/20 text-amber-200 px-1.5 py-0.1 rounded-full border border-amber-500/40">
                  <Sparkles className="w-2.5 h-2.5 text-amber-400" />
                  <span>需压过：{describeCombo(currentCombo)}</span>
                  {currentCombo.isBomb && <span className="text-rose-400 font-black">🔥</span>}
                </div>
              ) : (
                <div className="mt-0.5 text-[8px] text-amber-300 font-bold">
                  ✨ 新一墩 · 自由首发
                </div>
              )}
            </div>
          )}
        </div>

        {/* 4-Player East Drop Zone + East Seat */}
        {!is6p ? (
          <div className="flex items-center space-x-1">
            <div className="min-w-[45px] sm:min-w-[55px] flex items-center justify-center">
              {renderSeatTrickAction(1)}
            </div>
            {renderSeatAvatar(1)}
          </div>
        ) : (
          <div className="w-1" />
        )}
        </div>

        {/* South Player Action & Dealing Pile (Lower Felt Action Zone: Mathematically anchored at y ~ 80% directly in front of hand dock) */}
        <div className="w-full flex justify-center items-center pointer-events-none z-20 -mt-3 mb-0.5 min-h-[36px] sm:min-h-[44px]">
          {renderSeatTrickAction(0)}
        </div>
      </div>

      {/* Bottom Area: Action Bar & Hand Cards (Directly adjacent, tight, zero empty gap) */}
      <div className="relative z-20 px-1 pb-0.5 flex flex-col items-center w-full shrink-0">
        {/* iOS 3D Tactile Action Bar (Lowered and tightly hugging the hand cards dock) */}
        <div className="flex flex-wrap items-center justify-center gap-1 bg-slate-950/95 backdrop-blur-xl px-1.5 py-0.5 rounded-t-xl border-t border-x border-amber-500/40 shadow-xl z-30">
          <div className="flex items-center bg-slate-900 p-0.5 rounded-lg border border-slate-800 text-[9px] sm:text-[10px] font-black">
            <button
              onClick={() => handleSelectGroupingMode('natural')}
              className={`px-1.5 py-0.5 rounded transition-all ${
                groupingMode === 'natural' ? 'bg-amber-500 text-slate-950 font-black' : 'text-slate-400'
              }`}
            >
              1.大到小
            </button>
            <button
              onClick={() => handleSelectGroupingMode('coach')}
              className={`px-1.5 py-0.5 rounded transition-all ${
                groupingMode === 'coach' ? 'bg-amber-500 text-slate-950 font-black' : 'text-slate-400'
              }`}
            >
              2.教练建议
            </button>
            <button
              onClick={() => handleSelectGroupingMode('manual')}
              className={`px-1.5 py-0.5 rounded transition-all ${
                groupingMode === 'manual' ? 'bg-sky-500 text-slate-950 font-black' : 'text-slate-400'
              }`}
            >
              3.自选组牌
            </button>
          </div>

          {groupingMode === 'manual' && (
            <div className="flex items-center space-x-0.5">
              <button
                onClick={handleCreateCustomGroup}
                disabled={selectedIds.size === 0}
                className={`px-1.5 py-0.5 rounded text-[9px] font-black ${
                  selectedIds.size > 0 ? 'bg-sky-500 text-slate-950' : 'bg-slate-800 text-slate-500'
                }`}
              >
                编入组({selectedIds.size})
              </button>
              {customGroups.length > 0 && (
                <button
                  onClick={handleResetCustomGroups}
                  className="px-1 py-0.5 rounded text-[9px] text-rose-300 bg-rose-950/60"
                >
                  还原
                </button>
              )}
            </div>
          )}

          <button
            onClick={onAutoHint}
            disabled={!isMyTurn}
            className={`px-2 py-0.5 rounded-lg text-[10px] sm:text-xs font-black flex items-center space-x-0.5 ${
              isMyTurn ? 'bg-sky-500 hover:bg-sky-400 text-white' : 'bg-slate-800 text-slate-500'
            }`}
          >
            <Sparkles className="w-3 h-3" />
            <span>提示</span>
          </button>

          <button
            onClick={onPass}
            disabled={!isMyTurn || !currentCombo}
            className={`px-2 py-0.5 rounded-lg text-[10px] sm:text-xs font-black flex items-center space-x-0.5 ${
              isMyTurn && currentCombo ? 'bg-rose-600 hover:bg-rose-500 text-white' : 'bg-slate-800 text-slate-500'
            }`}
          >
            <X className="w-3 h-3" />
            <span>{t.passBtn}</span>
          </button>

          <button
            onClick={onPlay}
            disabled={!isMyTurn || selectedIds.size === 0}
            className={`px-3 sm:px-4 py-0.5 rounded-lg text-[11px] sm:text-xs font-black flex items-center space-x-1 ${
              isMyTurn && selectedIds.size > 0
                ? 'bg-gradient-to-r from-amber-400 to-amber-500 text-slate-950 shadow ring-1 ring-amber-200'
                : 'bg-slate-800 text-slate-500'
            }`}
          >
            <Zap className="w-3.5 h-3.5 fill-current" />
            <span>出牌 ({selectedIds.size})</span>
          </button>
        </div>

        {/* User Hand Cards View: Always Strictly In Viewport With Guaranteed Desktop Safe Margins */}
        <div className={`w-full flex justify-center items-end ${tableLayout.isCompactHeight ? 'min-h-[52px] pb-0.5' : 'min-h-[64px] sm:min-h-[76px] md:min-h-[88px] pb-1'} px-3 sm:px-6 md:px-8 overflow-visible shrink-0`}>
          {groupingMode === 'natural' && renderAdaptiveCards(naturalSortedHand)}

          {groupingMode === 'coach' && (
            bestGroups && bestGroups.length > 0 ? (
              renderGroupedCards(bestGroups.map(g => ({ cards: g.cards })))
            ) : (
              renderAdaptiveCards(naturalSortedHand)
            )
          )}

          {groupingMode === 'manual' && (
            customGroups.length > 0 || ungroupedCards.length > 0 ? (
              renderGroupedCards(
                [
                  ...customGroups.map((g) => ({ cards: g })),
                  ...(ungroupedCards.length > 0 ? [{ cards: ungroupedCards }] : []),
                ],
                true
              )
            ) : (
              renderAdaptiveCards(naturalSortedHand)
            )
          )}
        </div>
      </div>

      {/* Modals */}
      {showEmojiPicker && (
        <div className="absolute top-10 left-2 z-50 bg-slate-900/95 border border-amber-500/40 rounded-xl p-1.5 shadow-2xl space-y-1 max-w-xs">
          <div className="flex items-center justify-between text-xs font-black text-amber-300">
            <span>表情</span>
            <button onClick={() => setShowEmojiPicker(false)} className="text-slate-400">✕</button>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {POPULAR_EMOJIS.map((item, idx) => (
              <button key={idx} onClick={() => handleSelectEmoji(item)} className="p-1 rounded text-base bg-slate-800">
                {item.emoji}
              </button>
            ))}
          </div>
        </div>
      )}

      {showVoicePicker && (
        <div className="absolute top-10 left-2 z-50 bg-slate-900/95 border border-sky-500/40 rounded-xl p-1.5 shadow-2xl space-y-0.5 max-w-xs">
          <div className="flex items-center justify-between text-xs font-black text-sky-300">
            <span>实战牌语</span>
            <button onClick={() => setShowVoicePicker(false)} className="text-slate-400">✕</button>
          </div>
          <div className="space-y-0.5 max-h-36 overflow-y-auto">
            {POPULAR_PHRASES.map((phrase) => (
              <button
                key={phrase.id}
                onClick={() => handleSelectPhrase(phrase)}
                className="w-full text-left text-[10px] bg-slate-800 p-1 rounded text-slate-200"
              >
                {phrase.text}
              </button>
            ))}
          </div>
        </div>
      )}

      <CardBackCustomizerModal isOpen={showCardBackModal} onClose={() => setShowCardBackModal(false)} />
    </div>
  );
};
